import React from "react"
import { useUser } from "@clerk/clerk-react"
import { ApolloClient, ApolloProvider, InMemoryCache, HttpLink, split } from "@apollo/client"
import { GraphQLWsLink } from '@apollo/client/link/subscriptions'
import { createClient } from 'graphql-ws'
import { getMainDefinition } from '@apollo/client/utilities'
import { Routes, Route } from "react-router-dom"
import { ThemeProvider } from "@mui/material/styles"
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider"
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs"

import { theme } from "../theme"
import Header from "../components/Header"
import Login from "../components/Auth/Login"
import LoadUsers from "../components/LoadUsers"
import { drawerWidth } from "../constants"

import Dashboard from "../views/Dashboard/Dashboard"

import useAccessToken from "../hooks/useAccessToken"
import NavDraw from "../components/NavDraw"
import Farms from "./Farms/Farms"
import AddFarm from "./AddFarm"
import { CircularProgress, Grid } from "@mui/material"
import Farm from "./Farm"
import Collection from "./Collection/Collection"

// Determine the appropriate URL based on environment
// Set REACT_APP_USE_LOCAL_BACKEND=true in .env to use localhost
const useLocalBackend = process.env.REACT_APP_USE_LOCAL_BACKEND === 'true'

const HASURA_WS_URL = useLocalBackend
  ? "ws://localhost:8080/v1/graphql"
  : process.env.REACT_APP_HASURA_ENDPOINT_WSS

const HASURA_HTTP_URL = useLocalBackend
  ? "http://localhost:8080/v1/graphql"
  : process.env.REACT_APP_HASURA_ENDPOINT_WSS?.replace('wss://', 'https://')

const createApolloClient = (authToken, onForbidden) => {
  // For local development, use admin secret instead of JWT
  // Admin secret bypasses JWT verification in Hasura
  const headers = useLocalBackend
    ? { 
        'x-hasura-admin-secret': 'myadminsecretkey',
        'x-hasura-role': 'admin'
      }
    : { Authorization: `Bearer ${authToken}` }

  // Create HTTP link for queries and mutations
  const httpLink = new HttpLink({
    uri: HASURA_HTTP_URL,
    headers,
  })

  // Create WebSocket link for subscriptions
  const wsLink = new GraphQLWsLink(
    createClient({
      url: HASURA_WS_URL,
      connectionParams: () => ({
        headers,
      }),
      retryAttempts: 5,
      shouldRetry: () => true,
      retryWait: async (retries) => {
        // Exponential backoff: 1s, 2s, 4s, 8s, 16s
        await new Promise(resolve => setTimeout(resolve, Math.min(1000 * Math.pow(2, retries), 16000)))
      },
      on: {
        connected: () => console.log('WebSocket connected to:', HASURA_WS_URL),
        error: (error) => console.error('WebSocket error:', error),
        closed: (event) => {
          try {
            // graphql-ws may pass a CloseEvent or object containing code/reason
            const code = event?.code || (event && event.code) || null
            const reason = event?.reason || (event && event.reason) || ''
            console.log('WebSocket closed', code, reason)
            // Hasura returns 4403: Forbidden when JWT expires
            if (code === 4403 || /Forbidden/i.test(reason)) {
              console.warn('WebSocket closed with 4403 Forbidden — token likely expired')
              if (typeof onForbidden === 'function') onForbidden()
            }
          } catch (e) {
            console.log('Error handling closed event', e)
          }
        },
      },
    })
  )

  // Split links based on operation type
  const splitLink = split(
    ({ query }) => {
      const definition = getMainDefinition(query)
      return (
        definition.kind === 'OperationDefinition' &&
        definition.operation === 'subscription'
      )
    },
    wsLink,
    httpLink
  )

  return new ApolloClient({
    link: splitLink,
    cache: new InMemoryCache(),
  })
}

const App = () => {
  const [open, setOpen] = React.useState(false)

  const closeDraw = () => {
    setOpen(false)
  }
  const openDraw = () => {
    setOpen(true)
  }

  const [idToken, loadingToken, refreshToken] = useAccessToken()
  const { isLoaded, isSignedIn } = useUser()

  // Manage Apollo client in state so we can recreate it when needed
  const [client, setClient] = React.useState(null)
  const clientRef = React.useRef(null)
  const lastTokenRef = React.useRef(null)

  // Handler called when WS reports 4403 Forbidden — refresh token and recreate client
  const handleForbidden = React.useCallback(async () => {
    try {
      console.log('Attempting token refresh due to WS forbidden')
      await refreshToken()
      // Token refresh is now silent, but we recreate the client to update WS connection
      if (idToken && idToken !== lastTokenRef.current) {
        lastTokenRef.current = idToken
        const ap = createApolloClient(idToken, handleForbidden)
        setClient(ap)
        clientRef.current = ap
      }
    } catch (e) {
      console.error('Failed to refresh token after WS forbidden', e)
    }
  }, [refreshToken, idToken])

  // Recreate Apollo client only on initial load or when explicitly needed
  React.useEffect(() => {
    if (!idToken) {
      setClient(null)
      return
    }

    // Only recreate if token actually changed
    if (idToken === lastTokenRef.current) {
      return
    }

    lastTokenRef.current = idToken
    const ap = createApolloClient(idToken, handleForbidden)
    setClient(ap)
    clientRef.current = ap

    return () => {
      try {
        // attempt to cleanup websocket if possible
        if (ap && ap.stop) ap.stop()
      } catch (e) {
        // ignore
      }
    }
  }, [idToken, handleForbidden])

  if (!isLoaded || loadingToken) {
    return (
      <Grid container justifyContent="center">
        <Grid item>
          <CircularProgress />
        </Grid>
      </Grid>
    )
  }

  if (!isSignedIn || !idToken || !client) {
    return <Login />
  }

  return (
    <ApolloProvider client={client}>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <ThemeProvider theme={theme}>
          <Header openDraw={openDraw} open={open} />
          <NavDraw closeDraw={closeDraw} open={open} />

          <Grid
            container
            spacing={3}
            style={{
              marginTop: 75,
              ...(open && {
                width: `calc(100% - ${drawerWidth}px)`,
                marginLeft: `${drawerWidth}px`,
                transition: theme.transitions.create(["margin", "width"], {
                  easing: theme.transitions.easing.easeOut,
                  duration: theme.transitions.duration.enteringScreen,
                }),
              }),
            }}
          >
            <Grid item xs={12}>
              <Routes>
                <Route path="/farms" element={<Farms />} />
                <Route path="/add-farm" element={<AddFarm />} />
                <Route path="/users" element={<LoadUsers />} />
                <Route path="/" element={<Dashboard />} />
                <Route path="/collection" element={<Collection />} />
                <Route path="/farm/:id" element={<Farm />} />
              </Routes>
            </Grid>
          </Grid>
        </ThemeProvider>
      </LocalizationProvider>
    </ApolloProvider>
  )
}

export default App
